import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LocationOnOutlined,
  NotificationsActiveOutlined,
  PeopleAltOutlined,
  QueryStatsOutlined,
  ReceiptLongOutlined,
  CreditCardOutlined,
  WorkspacePremiumOutlined,
  AnalyticsOutlined,
  TrendingUpOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import "./admin.css";

// Import sub-modules
import VenuesModule from "./Admin/VenuesModule";
import PlansModule from "./Admin/PlansModule";
import SubscriptionsModule from "./Admin/SubscriptionsModule";
import PaymentsModule from "./Admin/PaymentsModule";
import UsersModule from "./Admin/UsersModule";
import ModelWeightageModule from "./Admin/ModelWeightageModule";
import NotificationsModule from "./Admin/NotificationsModule";
import PredictionAnalyticsModule from "./Admin/PredictionAnalyticsModule";
import PredictionStatsModule from "./Admin/PredictionStatsModule";
import ExportsModule from "./Admin/ExportsModule";

const ADMIN_TABS = [
  {
    id: "venues",
    label: "Venues",
    description: "Manage race venues, track metadata, and venue availability.",
    Icon: LocationOnOutlined,
  },
  {
    id: "plans",
    label: "Plans",
    description: "Configure subscription plans, billing rules, and access levels.",
    Icon: WorkspacePremiumOutlined,
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    description: "Monitor active subscriptions, renewals, cancellations, and user access.",
    Icon: ReceiptLongOutlined,
  },
  {
    id: "payments",
    label: "Payments",
    description: "Review payment transactions, billing status, refunds, and payment history.",
    Icon: CreditCardOutlined,
  },
  {
    id: "users",
    label: "Users",
    description: "Review user accounts, permissions, and account status.",
    Icon: PeopleAltOutlined,
  },
  {
    id: "model-weightage",
    label: "Model Weightage",
    description: "Display the latest model training feature weightage values.",
    Icon: QueryStatsOutlined,
  },
  {
    id: "notifications",
    label: "Notifications",
    description: "Compose, schedule, and review user notification campaigns.",
    Icon: NotificationsActiveOutlined,
  },
  {
    id: "prediction-analytics",
    label: "Prediction Analytics",
    description: "Review prediction analytical reports, model performance metrics, and accuracy trends.",
    Icon: AnalyticsOutlined,
  },
  {
    id: "prediction-stats",
    label: "Prediction Stats",
    description: "Manually update prediction statistics displayed on the landing page.",
    Icon: TrendingUpOutlined,
  },
  {
    id: "exports",
    label: "Exports",
    description: "Download prediction data and user data as formatted Excel files.",
    Icon: FileDownloadOutlined,
  },
];

function EmptyModule({ activeTab }) {
  return (
    <div
      className="min-h-[280px] grid content-center justify-items-center gap-3 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] text-center"
    >
      <p className="md:text-sm text-xs label-caps block m-0">
        {activeTab.label} Module
      </p>
      <h4
        className="m-0 xl:text-[28px] md:text-2xl text-xl text-[var(--color-text)]"
      >
        {activeTab.label} content will go here
      </h4>
      <p
        className="m-0 max-w-[520px] text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.6]"
      >
        {activeTab.description}
      </p>
    </div>
  );
}

const AdminPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTabId, setActiveTabId] = useState(() => {
    return location.state?.adminActiveTab || ADMIN_TABS[0].id;
  });

  useEffect(() => {
    if (location.state?.adminActiveTab !== activeTabId) {
      navigate(location.pathname, {
        state: { ...location.state, adminActiveTab: activeTabId },
        replace: true,
      });
    }
  }, [activeTabId, location.state, navigate, location.pathname]);

  const activeTab = useMemo(
    () => ADMIN_TABS.find((tab) => tab.id === activeTabId) || ADMIN_TABS[0],
    [activeTabId]
  );

  const ActiveIcon = activeTab.Icon;

  return (
    <section
      className="admin-panel-shell grid grid-cols-[minmax(200px,240px)_minmax(0,1fr)] gap-4 items-start"
    >
      <aside
        className="sticky xl:top-[130px] md:top-[110px] border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] overflow-hidden"
      >
        <div className="p-4 border-b border-[var(--color-border)]">
          <p className="md:text-sm text-xs label-caps block m-0">
            Admin
          </p>
          <h2
            className="mt-2 xl:text-[28px] md:text-2xl text-xl leading-[1.1] text-[var(--color-text)]"
          >
            Control Panel
          </h2>
        </div>

        <nav className="grid gap-2 p-4">
          {ADMIN_TABS.map(({ id, label, Icon }) => {
            const isActive = id === activeTabId;

            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTabId(id)}
                className={`flex items-center gap-3 w-full min-h-[44px] p-3 border rounded-[var(--radius-md)] cursor-pointer xl:text-base md:text-sm text-xs font-bold text-left transition-colors duration-200 ${isActive
                  ? "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]"
                  : "border-transparent bg-transparent text-[var(--color-text-muted)]"
                  }`}
              >
                <Icon sx={{ fontSize: 20 }} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <main
        className="min-w-0 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] overflow-hidden"
      >
        <div
          className="flex items-center justify-between gap-4 p-4 border-b border-[var(--color-border)]"
        >
          <div className="min-w-0">
            <p className="md:text-sm text-xs label-caps block m-0">
              Current Section
            </p>
            <h3
              className="mt-2 xl:text-[28px] md:text-2xl text-xl leading-[1.1] text-[var(--color-text)]"
            >
              {activeTab.label}
            </h3>
          </div>

          <div
            className="w-11 h-11 rounded-[var(--radius-md)] inline-flex items-center justify-center bg-[var(--color-primary)] text-black flex-shrink-0"
          >
            <ActiveIcon sx={{ fontSize: 24 }} />
          </div>
        </div>

        <div className="p-4">
          {activeTab.id === "venues" ? (
            <VenuesModule />
          ) : activeTab.id === "plans" ? (
            <PlansModule />
          ) : activeTab.id === "subscriptions" ? (
            <SubscriptionsModule />
          ) : activeTab.id === "payments" ? (
            <PaymentsModule />
          ) : activeTab.id === "users" ? (
            <UsersModule />
          ) : activeTab.id === "model-weightage" ? (
            <ModelWeightageModule />
          ) : activeTab.id === "notifications" ? (
            <NotificationsModule />
          ) : activeTab.id === "prediction-analytics" ? (
            <PredictionAnalyticsModule />
          ) : activeTab.id === "prediction-stats" ? (
            <PredictionStatsModule />
          ) : activeTab.id === "exports" ? (
            <ExportsModule />
          ) : (
            <EmptyModule activeTab={activeTab} />
          )}
        </div>
      </main>
    </section>
  );
};

export default AdminPanel;
